'use client'

import axios from 'axios'
import { AnimatePresence, motion, useInView } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { INSTAGRAM_MEDIA_URL } from '@/constants'
import { RevealText } from './reveal-text'

type Reel = {
  id: string
  caption?: string
  media_type: string
  media_url: string
  thumbnail_url?: string
  permalink: string
}

const INSTAGRAM_PROFILE = 'https://www.instagram.com/elementarycreative/'

export function InstagramSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-80px' })
  const [reels, setReels] = useState<Reel[]>([])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<Reel | null>(null)
  const [displayCount, setDisplayCount] = useState(9)

  useEffect(() => {
    const updateCount = () => setDisplayCount(window.innerWidth >= 768 ? 12 : 9)
    updateCount()
    window.addEventListener('resize', updateCount)
    return () => window.removeEventListener('resize', updateCount)
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await axios.get<Reel[]>(INSTAGRAM_MEDIA_URL)
        if (!cancelled) {
          setReels(res.data ?? [])
          setError('')
        }
      } catch {
        if (!cancelled) {
          setError('Could not load Instagram posts right now.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const loadMore = () => {
    const step = window.innerWidth >= 768 ? 4 : 3
    setDisplayCount((n) => Math.min(n + step, reels.length))
  }

  return (
    <section ref={sectionRef} className="border-t border-border px-6 py-32 md:py-48 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="mb-12 grid gap-8 md:mb-16 md:grid-cols-12">
          <div className="md:col-span-8">
            <RevealText className="mb-4">
              <span className="text-xs tracking-[0.3em] uppercase text-accent">On the feed</span>
            </RevealText>
            <RevealText delay={0.1}>
              <h2 className="font-serif text-4xl tracking-tight md:text-6xl">
                Instagram <span className="italic text-muted-foreground">reels & posts</span>
              </h2>
            </RevealText>
          </div>
          <div className="md:col-span-4 md:flex md:items-end">
            <RevealText delay={0.2}>
              <p className="font-serif text-lg italic text-muted-foreground">
                Recent work and behind-the-scenes from the studio.
              </p>
            </RevealText>
          </div>
        </div>

        {loading && (
          <motion.div
            className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:gap-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {Array.from({ length: displayCount }).map((_, i) => (
              <div
                key={i}
                className="aspect-square animate-pulse border border-border bg-muted/50"
              />
            ))}
          </motion.div>
        )}

        {!loading && error && (
          <p className="text-muted-foreground">
            {error}{' '}
            <a
              href={INSTAGRAM_PROFILE}
              target="_blank"
              rel="noreferrer"
              className="inline-block py-2 text-foreground underline-offset-4 hover:text-accent hover:underline"
            >
              View on Instagram
            </a>
          </p>
        )}

        {!loading && !error && reels.length > 0 && (
          <>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:gap-3">
              {reels.slice(0, displayCount).map((item, index) => (
                <motion.button
                  key={item.id}
                  type="button"
                  className="group relative aspect-square overflow-hidden border border-border bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  onClick={() => setSelected(item)}
                  initial={{ opacity: 0, y: 16 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
                  transition={{ duration: 0.5, delay: Math.min(index * 0.04, 0.35) }}
                  aria-label={item.caption ? `Open Instagram post: ${item.caption.slice(0, 60)}` : 'Open Instagram post'}
                >
                  <img
                    src={item.thumbnail_url || item.media_url}
                    alt={item.caption?.slice(0, 80) || 'Instagram post'}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  {item.media_type === 'VIDEO' && (
                    <span className="absolute inset-0 flex items-center justify-center bg-black/25">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/40 bg-black/50 text-white">
                        <svg viewBox="0 0 24 24" className="ml-0.5 h-4 w-4 fill-current" aria-hidden>
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </span>
                    </span>
                  )}
                </motion.button>
              ))}
            </div>

            {displayCount < reels.length && (
              <div className="mt-10 text-center">
                <button
                  type="button"
                  onClick={loadMore}
                  className="px-4 py-3 text-xs uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-accent"
                >
                  Load more
                </button>
              </div>
            )}
          </>
        )}

        {!loading && !error && reels.length === 0 && (
          <p className="text-muted-foreground">
            No posts to show.{' '}
            <a
              href={INSTAGRAM_PROFILE}
              target="_blank"
              rel="noreferrer"
              className="text-foreground underline-offset-4 hover:text-accent hover:underline"
            >
              Follow us on Instagram
            </a>
          </p>
        )}
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            className="modal-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => {
              if ((e.target as HTMLElement).classList.contains('modal-overlay')) setSelected(null)
            }}
          >
            <motion.div
              className="relative w-full max-w-3xl border border-border bg-background p-4 md:p-6"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center border border-border text-lg leading-none hover:border-accent"
                aria-label="Close"
              >
                ×
              </button>
              <motion.div className="flex max-h-[80vh] items-center justify-center pt-6">
                {selected.media_type === 'VIDEO' ? (
                  <video
                    className="max-h-[75vh] w-full object-contain"
                    src={selected.media_url}
                    controls
                    autoPlay
                    playsInline
                  />
                ) : (
                  <img
                    src={selected.media_url}
                    alt={selected.caption || 'Instagram media'}
                    className="max-h-[75vh] w-full object-contain"
                  />
                )}
              </motion.div>
              {selected.permalink && (
                <a
                  href={selected.permalink}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 inline-block py-2 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-accent"
                >
                  View on Instagram
                </a>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}
